import { Link, useLocation } from 'react-router-dom'
import { isInternalVisitor } from '../api/usage'
import { PlateSearch } from './PlateSearch'

/**
 * The pages on the main site this one links back to.
 *
 * This deployment is only the kentekencheck; the brand's other pages live on autochecken.com and
 * always will, so these are absolute and go to the real host rather than through any router. They
 * are the same three links, in the same order, that the previous site's header carried — a visitor
 * arriving here from a search result should be able to get to the rest of the brand from where they
 * landed.
 *
 * Deliberately not `target="_blank"`: it is the same brand, so it is a navigation and not a
 * departure.
 */
const SITE_LINKS: readonly { href: string; label: string }[] = [
  { href: 'https://www.autochecken.com/kies-je-auto', label: 'Kies je auto' },
  { href: 'https://www.autochecken.com/kies-je-merk', label: 'Kies je merk' },
  { href: 'https://www.autochecken.com/contact', label: 'Contact' },
]

/**
 * The main site's footer pages, linked from this one too.
 *
 * "Algemene voorwaarden" is the one that is not merely tidy. This site carries a paid affiliate
 * link and says so in the report; the terms behind that claim have to be reachable from the same
 * page, and the only copy of them lives on the main site.
 */
const FOOTER_LINKS: readonly { href: string; label: string }[] = [
  { href: 'https://www.autochecken.com/over-ons', label: 'Over ons' },
  { href: 'https://www.autochecken.com/adverteren-of-samenwerken', label: 'Adverteren' },
  { href: 'https://www.autochecken.com/contact', label: 'Contact' },
  { href: 'https://www.autochecken.com/algemene-voorwaarden', label: 'Algemene voorwaarden' },
]

/**
 * The red bar: the brand, the links back to the main site, and the compact plate search on the
 * right so a second lookup is never more than one field away.
 *
 * The brand still points at this app's own home rather than at autochecken.com. A logo is the way
 * back to where you are, and sending it off-site would leave a visitor on the report page with no
 * route to a fresh search but the box beside it.
 *
 * **"Gratis kentekencheck:" is doing a job, not labelling a field.** The search box is already
 * obviously a search box; what a visitor does not know is that the answer costs nothing, and this
 * page is competing with several that charge for it. It is therefore not the input's label — that
 * one is `visually-hidden` inside `PlateSearch` and says "Kenteken" — and it stays visible at every
 * width, which is why the nav scrolls sideways on a narrow screen rather than this being the thing
 * that gives way.
 */
export function SiteHeader() {
  const { pathname } = useLocation()

  return (
    <header className="site-header">
      <div className="container site-header__inner">
        <Link className="site-header__brand" to="/">
          AUTOCHECKEN.COM
        </Link>

        <nav className="site-header__nav" aria-label="AutoChecken">
          {SITE_LINKS.map((link) => (
            <a className="site-header__link" href={link.href} key={link.href}>
              {link.label}
            </a>
          ))}
        </nav>

        {/* On the home page the hero already owns a search box; a second one is just noise. */}
        {pathname !== '/' && (
          <div className="site-header__search">
            <span className="site-header__search-label" aria-hidden="true">
              Gratis kentekencheck:
            </span>
            <PlateSearch variant="compact" />
          </div>
        )}
      </div>
    </header>
  )
}

export function SiteFooter() {
  return (
    <footer className="site-footer">
      <div className="container">
        <div className="site-footer__grid">
          <strong>AutoChecken</strong>
          {FOOTER_LINKS.map((link) => (
            <a href={link.href} key={link.href}>
              {link.label}
            </a>
          ))}
          {/*
            "RDW" rather than "RDW Open Data", and rdw.nl rather than opendata.rdw.nl. The credit is
            worth keeping — it is the whole basis of the site's claim to be accurate — but "Open
            Data" would double as a signpost to the free dataset every figure here comes from,
            which is a strange thing to put in your own footer. The data is public domain, so there
            is no attribution obligation being traded away.
          */}
          <a href="https://www.rdw.nl/" target="_blank" rel="noreferrer noopener">
            Bron: RDW
          </a>
        </div>
        {/*
          The first two sentences match the wording on autochecken.com so the two properties say the
          same thing. The trademark sentence is this site's own: the report heading shows the make's
          logo, and this line keeps that a reference to the make rather than a suggested
          affiliation. Keep it short: on a page that already ends in a red band, a paragraph of small
          print makes the footer taller than anything it contains.

          It does not account for the parts that come from outside the RDW, and does not need to: the
          review and the battery figures each carry their own credit at the card they belong to,
          which is where the licence requires it and where a reader is looking when it matters.

          The year is computed rather than typed, or it goes stale the way the main site's has.
        */}
        <p className="site-footer__note">
          De voertuiggegevens komen van de RDW en worden ongewijzigd weergegeven. AutoChecken.com is
          niet aansprakelijk voor (gevolg)schade die voortkomt uit het gebruik van deze site, dan wel
          uit fouten of ontbrekende informatie op deze site. Merknamen en logo's zijn eigendom van
          hun respectieve eigenaren; AutoChecken is niet verbonden aan deze merken. Copyright ©{' '}
          {new Date().getFullYear()} AutoChecken.com. Alle rechten voorbehouden.
        </p>
        {/*
          Only ever seen by the owner, on a browser opted out with ?intern=1. Without it there is no
          way to tell from the page whether the flag took, and a test session that was counted after
          all is exactly what the opt-out exists to prevent. Read once per render; the flag only
          changes on a page load.
        */}
        {isInternalVisitor() && (
          <p className="site-footer__note">Intern: bezoeken vanaf dit apparaat worden niet meegeteld.</p>
        )}
      </div>
    </footer>
  )
}
